import { ArrowUndoRegular, ArrowSyncRegular, bundleIcon, PremiumFilled, PremiumRegular, SettingsRegular, ArrowSyncFilled, SettingsFilled, } from "@fluentui/react-icons";
import { CalendarTodoRegular, CalendarTodoFilled, TeamsRegular, TeamsFilled, } from "@fluentui/react-brand-icons";
import { getCoprincipalMailboxInfoForMailboxInfo } from "owa-account-source-list-store";
import { getIndexerValueForMailboxInfo } from "owa-client-types";
import { isUnifiedInboxMailboxInfo } from "owa-client-types/lib/UnifiedInboxMailboxInfo";
import { getUnseenItemsCount as getUnseenNotificationsCount } from "owa-activity-feed/lib/store/unseenItemsSelector";
import { logCoreUsage, logUsage } from "owa-analytics";
import getDrawerPaneId from "owa-app-pane-group-utils/lib/getDrawerPaneId";
import { isCapabilityEnabled } from "owa-capabilities";
import { rewardsBadgeCapability } from "owa-capabilities-definitions/lib/rewardsBadgeCapability";
import { isDrawerPaneVisible } from "owa-drawer";
import { isGlobalFeatureEnabled } from "owa-feature-flags";
import isWin32StylingEnabled from "owa-theme-win32-style-store/lib/selectors/isWin32StylingEnabled";
import type { OwaHeaderButtonControls } from "owa-header-types";
import loc from "owa-localize";
import { getCurrentModule } from "owa-app-module-store";
import { getModuleContextMailboxInfo } from "owa-module-context-mailboxinfo";
import areNotificationsDisabled from "owa-notifications-settings/lib/selectors/areNotificationsDisabled";
import { lazyMountAndShowFullOptions } from "owa-options-view";
import { isPremiumConsumer } from "owa-session-store";
import { getUnreadConversationsCount as getTeamsUnreadConversationsCount } from "owa-teams-embed-client-store";
import { isTimePanelAvailable } from "owa-time-panel-bootstrap";
import { lazyOpenPremiumDashboard } from "owa-whats-new";
import { MY_DAY_BUTTON_ID, NOTIFICATIONS_BUTTON_ID, PREMIUM_BUTTON_ID, BUY_MICROSOFT365_BUTTON_ID, BUY_MICROSOFT365_MAX_WIDTH, REWARDS_BUTTON_ID, SETTINGS_BUTTON_ID, TEAMS_BUTTON_ID, SYNC_BUTTON_ID, UNDO_BUTTON_ID, } from "../constants/constants";
import onMyDayButtonClicked from "../utils/onMyDayButtonClicked";
import isTeamsChatButtonEnabled from "../utils/isTeamsChatButtonEnabled";
import { lazyOnMicrosoft365UpsellButtonClicked } from "owa-header-microsoft365-upsell-click";
import { getMicrosoft365UpsellDimeConfig, getMicrosoft365UpsellMode, getMicrosoft365UpsellIrisContent, invalidateMicrosoft365UpsellEligibilityAfterPurchase, isMicrosoft365UpgradeMode, isMicrosoft365UpsellVisible, } from "../utils/getMicrosoft365UpsellMode";
import { logPersistentHeaderButtonHovered, logPersistentHeaderButtonShown, } from "../utils/logPersistentMicrosoft365UpsellTelemetry";
import onSyncButtonClicked from "../utils/onSyncButtonClicked";
import onUndoButtonClicked from "../utils/onUndoButtonClicked";
import renderNotificationsIcon from "../utils/renderNotificationsIcon";
import { NotificationsPane, TeamsChatPane } from "owa-header-panes";
import { RewardsPane } from "owa-header-rewards-pane";
import { myDayHeaderButton, notificationsDisabledHeaderButton, notificationsHeaderButton, notificationsHeaderButtonWithCount, premiumHeaderButton, manageSubscriptionHeaderButton, buyMicrosoft365HeaderButton, upgradeMicrosoft365HeaderButton, rewardsHeaderButton, settingsHeaderButton, syncHeaderButton, syncHeaderButtonTooltip, teamsChatHeaderButton, teamsChatHeaderButtonWithCount, undoHeaderButton, undoHeaderButtonTooltip, } from "./OwaHeaderButton.locstring.json";
import { brandIcon } from "./headerButtons.scss";
import { RewardsMedallionIcon, RewardsMedallionWidget } from "owa-header-rewards-medallion";
import { getRewardsAccountKey, getRewardsButtonWidth, setRewardsButtonVisibleInHeaderMutator, } from "owa-header-rewards-medallion-store";

const TeamsIcon = bundleIcon(TeamsFilled, TeamsRegular);
const MyDayIcon = bundleIcon(CalendarTodoFilled, CalendarTodoRegular);
const SyncIcon = bundleIcon(ArrowSyncFilled, ArrowSyncRegular);
const SettingsIcon = bundleIcon(SettingsFilled, SettingsRegular);
const PremiumIcon = bundleIcon(PremiumFilled, PremiumRegular);

const isMailModule = () => getCurrentModule() === "Mail";

/*
 * The order of the buttons in this array is important.
 * The buttons are rendered in the order they are defined here from left to right.
 *
 * Most buttons (Teams, MyDay, Rewards, Settings, etc.) are gated behind isWin32StylingEnabled()
 * being OFF to preserve the classic OWA header when the overlay is disabled.
 * Sync and Undo are the inverse: they are shown only when the Win32 overlay is ON.
 * Notifications is always visible.
 */

export const headerButtons: OwaHeaderButtonControls = [
    {
        id: PREMIUM_BUTTON_ID,
        renderLabel: () => loc(isGlobalFeatureEnabled("growth-subscriptioninstringsinsteadofpremium")
            ? manageSubscriptionHeaderButton
            : premiumHeaderButton),
        renderIcon: () => <PremiumIcon />,
        onClick: () => {
            const mailboxInfo = getModuleContextMailboxInfo();
            if (isUnifiedInboxMailboxInfo(mailboxInfo)) {
                return;
            }
            
            logUsage("HeaderPremiumDiamondClicked", mailboxInfo, undefined);
            lazyOpenPremiumDashboard.importAndExecute();
        },
        isVisible: () => {
            const mailboxInfo = getModuleContextMailboxInfo();
            return (!isWin32StylingEnabled() &&
                !isUnifiedInboxMailboxInfo(mailboxInfo) &&
                isPremiumConsumer(mailboxInfo));
        },
        overflowItem: {
            priority: 2,
            divider: "after",
        },
    },
    {
        id: TEAMS_BUTTON_ID,
        renderLabel: () => loc(teamsChatHeaderButton),
        renderIcon: () => <TeamsIcon className={brandIcon}/>,
        isVisible: () => !isWin32StylingEnabled() && isTeamsChatButtonEnabled(),
        pane: {
            id: getDrawerPaneId("teamsChat"),
            render: id => <TeamsChatPane paneId={id}/>,
        },
        badge: {
            getCount: () => {
                return getTeamsUnreadConversationsCount();
            },
        },
        renderTooltip: () => {
            const unreadCount = getTeamsUnreadConversationsCount();
            return unreadCount > 0
                ? loc(teamsChatHeaderButtonWithCount, unreadCount)
                : loc(teamsChatHeaderButton);
        },
        overflowItem: {
            priority: 1,
        },
    },
    {
        id: MY_DAY_BUTTON_ID,
        renderLabel: () => loc(myDayHeaderButton),
        renderIcon: () => <MyDayIcon />,
        onClick: () => onMyDayButtonClicked(),
        isVisible: () => !isWin32StylingEnabled() && isTimePanelAvailable(),
        overflowItem: {
            priority: 3,
        },
    },
    {
        id: REWARDS_BUTTON_ID,
        renderLabel: () => loc(rewardsHeaderButton),
        renderIcon: location => {
            if (location === "overflowMenu") {
                return <RewardsMedallionIcon />;
            }
            const mailboxInfo = getModuleContextMailboxInfo();
            return <RewardsMedallionWidget key={getIndexerValueForMailboxInfo(mailboxInfo)}/>;
        },
        contentWidth: () => getRewardsButtonWidth(getModuleContextMailboxInfo()),
        headerVisibilityContextKey: () => getRewardsAccountKey(getModuleContextMailboxInfo()),
        onHeaderVisibilityChange: isVisible => {
            const rewardsAccountKey = getRewardsAccountKey(getModuleContextMailboxInfo());
            setRewardsButtonVisibleInHeaderMutator(rewardsAccountKey, isVisible);
        },
        headerSpacingBefore: 16,
        onClick: () => {
            const moduleMailboxInfo = getModuleContextMailboxInfo();
            const telemetryMailboxInfo = getCoprincipalMailboxInfoForMailboxInfo(moduleMailboxInfo) ?? moduleMailboxInfo;
            const action = isDrawerPaneVisible(getDrawerPaneId("rewards")) ? "close" : "open";
            logCoreUsage("HeaderRewardsButtonClicked", telemetryMailboxInfo, { action }, {
                isScorecardEvent: true,
            });
        },
        onHover: () => {
            const moduleMailboxInfo = getModuleContextMailboxInfo();
            const telemetryMailboxInfo = getCoprincipalMailboxInfoForMailboxInfo(moduleMailboxInfo) ?? moduleMailboxInfo;
            logUsage("HeaderRewardsButtonHovered", telemetryMailboxInfo);
        },
        pane: {
            id: getDrawerPaneId("rewards"),
            render: id => <RewardsPane paneId={id}/>,
        },
        // Reads the currently-viewed (module context) account. Visibility re-evaluates live on an
        // in-app account switch because useHeaderButtonVisibility runs isVisible() inside a MobX
        // `computed` within the observer OwaHeaderButtonGroup, so this observable access is tracked.
        isVisible: () => !isWin32StylingEnabled() &&
            isCapabilityEnabled(rewardsBadgeCapability, getModuleContextMailboxInfo()),
        renderTooltip: () => loc(rewardsHeaderButton),
        // Pin Rewards next to the bell so it stays visible (out of the overflow menu) for the current
        // account when its capability is enabled.
        overflowItem: {
            priority: 6,
            addsHeaderSlot: true,
        },
    },
    {
        id: BUY_MICROSOFT365_BUTTON_ID,
        renderLabel: () => {
            const mailboxInfo = getModuleContextMailboxInfo();
            return (getMicrosoft365UpsellIrisContent(mailboxInfo)?.buttonText ??
                (isMicrosoft365UpgradeMode(getMicrosoft365UpsellMode(mailboxInfo))
                    ? loc(upgradeMicrosoft365HeaderButton)
                    : loc(buyMicrosoft365HeaderButton)));
        },
        renderIcon: () => <PremiumIcon />,
        // Render the label text inline next to the diamond so the offer is legible without hovering.
        showLabel: true,
        // Cap the button width and let the browser size the content, so it fits shorter/longer
        // translations without a fragile per-character width calculation; longer labels truncate
        // with an ellipsis rather than overflowing or overlapping neighboring controls.
        contentWidth: BUY_MICROSOFT365_MAX_WIDTH,
        onClick: () => {
            const mailboxInfo = getModuleContextMailboxInfo();
            const upsellMode = getMicrosoft365UpsellMode(mailboxInfo);
            const dimeConfig = getMicrosoft365UpsellDimeConfig(mailboxInfo);
            if (upsellMode !== "hidden" && dimeConfig) {
                lazyOnMicrosoft365UpsellButtonClicked.importAndExecute(upsellMode, mailboxInfo, dimeConfig, () => invalidateMicrosoft365UpsellEligibilityAfterPurchase(mailboxInfo));
            }
        },
        onShow: () => logPersistentHeaderButtonShown(getModuleContextMailboxInfo()),
        onHover: () => logPersistentHeaderButtonHovered(getModuleContextMailboxInfo()),
        // Visibility and Buy/Upgrade routing share the eligibility mode. When available, IRIS
        // overrides the displayed text and DIME configuration; otherwise the mode selects the
        // existing localized text and DIME defaults.
        isVisible: () => isMicrosoft365UpsellVisible(getModuleContextMailboxInfo()),
        renderTooltip: () => {
            const mailboxInfo = getModuleContextMailboxInfo();
            return (getMicrosoft365UpsellIrisContent(mailboxInfo)?.buttonText ??
                (isMicrosoft365UpgradeMode(getMicrosoft365UpsellMode(mailboxInfo))
                    ? loc(upgradeMicrosoft365HeaderButton)
                    : loc(buyMicrosoft365HeaderButton)));
        },
        // Pin the persistent upsell to the left of the notifications bell so it stays visible (out
        // of the overflow menu) for free consumer accounts when the flight is enabled.
        overflowItem: {
            priority: 7,
            addsHeaderSlot: true,
        },
    },
    {
        id: NOTIFICATIONS_BUTTON_ID,
        renderLabel: () => loc(notificationsHeaderButton),
        renderIcon: renderNotificationsIcon,
        pane: {
            id: getDrawerPaneId("notifications"),
            render: id => <NotificationsPane paneId={id}/>,
        },
        badge: {
            getCount: () => getUnseenNotificationsCount(),
        },
        renderTooltip: () => {
            if (areNotificationsDisabled()) {
                return loc(notificationsDisabledHeaderButton);
            }
            const unseenCount = getUnseenNotificationsCount();
            return unseenCount > 0
                ? loc(notificationsHeaderButtonWithCount, unseenCount)
                : loc(notificationsHeaderButton);
        },
        overflowItem: {
            priority: 5,
        },
    },
    {
        id: SYNC_BUTTON_ID,
        renderLabel: () => loc(syncHeaderButton),
        renderIcon: () => <SyncIcon />,
        overlayPlacement: "left",
        onClick: onSyncButtonClicked,
        isVisible: () => (isWin32StylingEnabled() ||
            isGlobalFeatureEnabled("fwk-header-sync-button-and-cleanup")) &&
            isMailModule(),
        renderTooltip: () => loc(syncHeaderButtonTooltip),
        overflowItem: {
            priority: 0,
        },
    },
    {
        id: UNDO_BUTTON_ID,
        renderLabel: () => loc(undoHeaderButton),
        renderIcon: () => <ArrowUndoRegular />,
        overlayPlacement: "left",
        onClick: onUndoButtonClicked,
        isVisible: () => isWin32StylingEnabled() && isMailModule(),
        renderTooltip: () => loc(undoHeaderButtonTooltip),
        overflowItem: {
            priority: 0,
        },
    },
    {
        id: SETTINGS_BUTTON_ID,
        renderLabel: () => loc(settingsHeaderButton),
        renderIcon: () => <SettingsIcon />,
        onClick: () => lazyMountAndShowFullOptions.importAndExecute(),
        isVisible: () => !isWin32StylingEnabled(),
        overflowItem: {
            priority: 4,
            divider: "before",
        },
    },
] as const;
